/**
 * src/components/mfa/MfaChallenge.tsx
 * 追加認証の入力部分（ログイン後の追加認証画面 / 設定変更時の再認証ダイアログで共用）
 *   - パスキー: 端末の認証ダイアログ（Windows Hello / 指紋・顔認証 / PIN / セキュリティキー）を起動
 *   - メール : 6桁コード入力（再送・残り回数・有効期限の案内つき）
 * どちらも検証はサーバー側。ここでは結果を表示するだけ。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { MfaApiError, mfaFetch } from '../../api/mfa'
import {
  describePasskeyError, getPasskeyAssertion, isPasskeySupported, PasskeyCancelled,
} from '../../lib/webauthn'

type Props = {
  method: 'passkey' | 'email'
  mode: 'login' | 'stepup'
  emailMasked?: string | null
  /** ログインAPIが既にコードを送信済みか（email / login） */
  initialSent?: boolean
  initialSendError?: string | null
  initialResendAfter?: number
  onSuccess: (data: any) => void
  /** セッションが失効した/失敗上限で破棄された */
  onSessionLost?: () => void
}

const box: React.CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--paper-3)', borderRadius: 10, padding: '14px 16px',
}

export default function MfaChallenge({
  method, mode, emailMasked, initialSent, initialSendError, initialResendAfter, onSuccess, onSessionLost,
}: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(initialSendError ?? null)
  const [notice, setNotice] = useState<string | null>(initialSent ? 'sent' : null)
  const [code, setCode] = useState('')
  const [resendIn, setResendIn] = useState(initialSent ? (initialResendAfter ?? 60) : (initialResendAfter ?? 0))
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)
  const autoRan = useRef(false)

  const handleErr = useCallback((e: unknown, fallback: string) => {
    if (e instanceof MfaApiError) {
      if (e.code === 'MFA_SESSION_LOCKED' || e.code === 'NO_SESSION' || e.code === 'WRONG_SESSION_STATE'
        || (e.status === 401 && e.code === 'MFA_REQUIRED')) {
        onSessionLost?.()
        setError('失敗が続いたため、最初からやり直してください。')
        return
      }
      if (typeof e.data.retry_after_sec === 'number' && (e.code === 'RESEND_TOO_SOON' || e.code === 'TOO_MANY_SENDS')) {
        setResendIn(e.data.retry_after_sec)
      }
      if (typeof e.data.attempts_left === 'number') setAttemptsLeft(e.data.attempts_left)
      setError(e.message || fallback)
      return
    }
    setError(describePasskeyError(e) || fallback)
  }, [onSessionLost])

  // ── パスキー ────────────────────────────────────────────
  const runPasskey = useCallback(async (silent = false) => {
    setError(null)
    setBusy(true)
    try {
      const options = await mfaFetch('/auth/mfa/challenge/passkey/options', 'POST', {})
      const response = await getPasskeyAssertion(options)
      const data = await mfaFetch('/auth/mfa/challenge/passkey/verify', 'POST', { response })
      onSuccess(data)
    } catch (e) {
      const cancelled = e instanceof PasskeyCancelled || (e as any)?.name === 'NotAllowedError'
      if (silent && cancelled) setNotice('manual')   // 自動起動が拒否された → ボタンで再試行してもらう
      else handleErr(e, 'パスキーの認証に失敗しました。')
    } finally {
      setBusy(false)
    }
  }, [handleErr, onSuccess])

  useEffect(() => {
    if (method !== 'passkey' || autoRan.current || !isPasskeySupported()) return
    autoRan.current = true
    void runPasskey(true)   // ログイン成功直後に端末の認証ダイアログを自動で起動
  }, [method, runPasskey])

  // ── メール ───────────────────────────────────────────────
  const sendCode = useCallback(async () => {
    setError(null)
    setBusy(true)
    try {
      const r = await mfaFetch('/auth/mfa/challenge/email/send', 'POST', {})
      setNotice('sent')
      setResendIn(r.resend_after_sec ?? 60)
      setCode('')
      setAttemptsLeft(null)
    } catch (e) {
      handleErr(e, 'コードの送信に失敗しました。')
    } finally {
      setBusy(false)
    }
  }, [handleErr])

  useEffect(() => {
    // 再認証ダイアログ（stepup）では開いた時点でコードを送る。ログイン時はログインAPIが送信済み
    if (method === 'email' && mode === 'stepup' && !autoRan.current) {
      autoRan.current = true
      void sendCode()
    }
  }, [method, mode, sendCode])

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((s) => Math.max(0, s - 1)), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!/^\d{6}$/.test(code)) { setError('6桁の数字を入力してください。'); return }
    setError(null)
    setBusy(true)
    try {
      const data = await mfaFetch('/auth/mfa/challenge/email/verify', 'POST', { code })
      onSuccess(data)
    } catch (err) {
      setCode('')
      handleErr(err, 'コードの確認に失敗しました。')
    } finally {
      setBusy(false)
    }
  }

  const errorBox = error && (
    <div role="alert" style={{
      background: 'var(--red-light)', border: '1px solid rgba(192,57,43,0.25)', color: 'var(--red)',
      borderRadius: 8, padding: '10px 14px', fontSize: 13, margin: '12px 0', lineHeight: 1.6,
    }}>⚠ {error}</div>
  )

  if (method === 'passkey') {
    const supported = isPasskeySupported()
    return (
      <div>
        <div style={{ ...box, marginBottom: 4 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>🔐 パスキーで本人確認</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7 }}>
            端末の認証画面（Windows Hello・指紋/顔認証・PIN・セキュリティキー）が開きます。
            {mode === 'login' ? '認証すると、ログインが完了します。' : '認証すると、続けて操作できます。'}
          </div>
        </div>
        {!supported && (
          <div style={{ fontSize: 12.5, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', margin: '12px 0', lineHeight: 1.7 }}>
            このブラウザ/端末ではパスキーを利用できません。パスキーを登録した端末（またはスマートフォン）で開くか、
            最新の Chrome・Edge・Safari をお使いください。解決しない場合は管理者に連絡してください。
          </div>
        )}
        {notice === 'manual' && !error && (
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '12px 0' }}>
            下のボタンを押すと、端末の認証画面が開きます。
          </div>
        )}
        {errorBox}
        <button type="button" className="btn btn-primary" disabled={busy || !supported}
          onClick={() => void runPasskey(false)}
          style={{ display: 'block', width: '100%', height: 48, fontSize: 15, fontWeight: 600, borderRadius: 10, marginTop: 12 }}>
          {busy ? '認証中...' : 'パスキーで認証する'}
        </button>
        <details style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-2)' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>パソコンで、スマホのパスキーを使うには</summary>
          <ol style={{ lineHeight: 1.9, paddingLeft: 20, margin: '8px 0 0' }}>
            <li>「Windows Hello」の画面が先に出たら、<strong>キャンセル</strong>するか、「別の方法」「スマートフォン／別のデバイスを使用」を選びます。</li>
            <li>パソコンに<strong>QRコード</strong>が出ます。スマホの<strong>カメラ</strong>で読み取ります。</li>
            <li>スマホの<strong>顔認証・指紋</strong>で認証します。</li>
          </ol>
          <ul style={{ lineHeight: 1.9, paddingLeft: 18, margin: '8px 0 0', color: 'var(--ink-3)' }}>
            <li>QRで認証できないときは、パソコンとスマホの<strong>Bluetoothをオン</strong>にして、近づけてやり直します。</li>
            <li>それでもできないときは、<strong>スマホのブラウザでMangaOSを開いて</strong>ログインしてください。</li>
            <li>共用パソコンでは、パスワードをブラウザに保存せず、使い終わったらログアウトしてください。</li>
          </ul>
        </details>
      </div>
    )
  }

  return (
    <form onSubmit={verifyCode}>
      <div style={{ ...box }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>✉️ メールの6桁コードで本人確認</div>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7 }}>
          {notice === 'sent'
            ? <>登録メールアドレス{emailMasked ? <strong>（{emailMasked}）</strong> : ''}に6桁のコードを送りました。有効期限は5分です。</>
            : <>登録メールアドレス{emailMasked ? <strong>（{emailMasked}）</strong> : ''}にコードを送ります。</>}
        </div>
      </div>

      <label htmlFor="mfa-code" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', margin: '16px 0 6px' }}>
        認証コード（6桁）
      </label>
      <input
        id="mfa-code"
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6}
        placeholder="123456" disabled={busy}
        style={{
          display: 'block', width: '100%', boxSizing: 'border-box', height: 52, padding: '0 14px',
          fontSize: 24, letterSpacing: '0.4em', textAlign: 'center', fontFamily: 'var(--font-mono)',
          background: 'var(--paper)', color: 'var(--ink)', border: '1.5px solid var(--paper-3)', borderRadius: 8, outline: 'none',
        }}
      />
      {attemptsLeft !== null && attemptsLeft >= 0 && !error && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>残り入力回数: {attemptsLeft}回</div>
      )}
      {errorBox}

      <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}
        style={{ display: 'block', width: '100%', height: 48, fontSize: 15, fontWeight: 600, borderRadius: 10, marginTop: 12 }}>
        {busy ? '確認中...' : '確認する'}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-outline btn-sm" disabled={busy || resendIn > 0}
          onClick={() => void sendCode()} style={{ minHeight: 36 }}>
          {resendIn > 0 ? `コードを再送する（${resendIn}秒後）` : notice === 'sent' ? 'コードを再送する' : 'コードを送る'}
        </button>
      </div>
      <ul style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.8, margin: '12px 0 0', paddingLeft: 18 }}>
        <li>再送すると、前のコードは使えなくなります。<strong>最後に届いたメール</strong>のコードを入力してください。</li>
        <li>メールが届かないときは、迷惑メールフォルダを確認し、数分待ってから再送してください。</li>
        <li>何度も届かない場合は、管理者に連絡してください（認証方式の再設定ができます）。</li>
      </ul>
    </form>
  )
}
